'use client';

import { Suspense, useState } from 'react';
import { signIn } from 'next-auth/react';
import { useRouter, useSearchParams } from 'next/navigation';
import Link from 'next/link';

const roles = [
  { id: 'property-manager', label: 'Property Manager', icon: '🏢' },
  { id: 'staff',            label: 'Staff Member',      icon: '🔧' },
  { id: 'tenant',           label: 'Tenant',            icon: '🏠' },
  { id: 'super-admin',      label: 'Super Admin',       icon: '⚡' },
] as const;

const demoAccounts = [
  { email: 'admin@propmatic.co.za',   role: 'Super Admin',       id: 'super-admin' },
  { email: 'manager@propmatic.co.za', role: 'Property Manager',  id: 'property-manager' },
  { email: 'tenant@propmatic.co.za',  role: 'Tenant',            id: 'tenant' },
  { email: 'staff@propmatic.co.za',   role: 'Staff',             id: 'staff' },
] as const;

function LoginContent() {
  const router      = useRouter();
  const searchParams = useSearchParams();
  const callbackUrl  = searchParams.get('callbackUrl');

  const [email,        setEmail]        = useState('');
  const [password,     setPassword]     = useState('');
  const [selectedRole, setSelectedRole] = useState<string>('property-manager');
  const [showPassword, setShowPassword] = useState(false);
  const [error,        setError]        = useState('');
  const [loading,      setLoading]      = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await signIn('credentials', { email, password, redirect: false });
    setLoading(false);

    if (result?.error) {
      const check = await fetch(`/api/auth/check-inactive?email=${encodeURIComponent(email)}`);
      const { pending } = await check.json().catch(() => ({ pending: false }));
      setError(pending
        ? 'Your account is pending approval. You will be notified once approved.'
        : 'Invalid email or password.');
      return;
    }

    router.push(callbackUrl ?? `/dashboard/${selectedRole}`);
    router.refresh();
  }

  const activeRole = roles.find(r => r.id === selectedRole);

  return (
    <div className="min-h-screen bg-[#09090B] flex items-center justify-center p-4">
      <div className="w-full max-w-md">

        {/* Logo + headline */}
        <div className="mb-7">
          <Link href="/landing" className="inline-flex items-center gap-2 mb-5">
            <div className="w-6 h-6 rounded-md bg-[rgba(124,58,237,0.2)] border border-[rgba(124,58,237,0.3)] flex items-center justify-center">
              <span className="text-[10px] font-bold text-[#A78BFA]">L</span>
            </div>
            <span className="font-manrope font-bold text-[#EEEEF0] text-sm tracking-tight">Propmatic</span>
          </Link>
          <h1 className="font-manrope font-extrabold text-2xl text-[#EEEEF0] tracking-tight mb-1">Sign in</h1>
          <p className="text-xs font-mono text-[#5A5A6E]">Access your property management portal</p>
        </div>

        {/* Card */}
        <div className="bg-[#0D0D12] border border-[rgba(124,58,237,0.2)] rounded-2xl shadow-[0_0_60px_rgba(124,58,237,0.1)] overflow-hidden">
          <div className="h-px w-full bg-linear-to-r from-transparent via-[#A78BFA] to-transparent" />
          <div className="p-6 space-y-5">

            {/* Role selector */}
            <div>
              <label className="text-[10px] font-mono text-[#5A5A6E] uppercase tracking-widest mb-2 block">Sign in as</label>
              <div className="grid grid-cols-2 gap-2">
                {roles.map(role => (
                  <button key={role.id} type="button" onClick={() => setSelectedRole(role.id)}
                    className={`flex items-center gap-2.5 px-3 py-2.5 rounded-xl border text-left transition-all duration-200 ${
                      selectedRole === role.id
                        ? 'border-[rgba(124,58,237,0.4)] bg-[rgba(124,58,237,0.1)]'
                        : 'border-[rgba(255,255,255,0.05)] bg-[#1C1C24] hover:border-[rgba(255,255,255,0.1)]'
                    }`}
                  >
                    <span className="text-base leading-none">{role.icon}</span>
                    <span className={`text-xs font-manrope font-medium leading-tight ${selectedRole === role.id ? 'text-[#EEEEF0]' : 'text-[#9999A8]'}`}>
                      {role.label}
                    </span>
                    {selectedRole === role.id && (
                      <span className="ml-auto w-1.5 h-1.5 rounded-full shrink-0 bg-[#A78BFA]" />
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Email */}
              <div>
                <label htmlFor="login-email" className="text-[10px] font-mono text-[#5A5A6E] uppercase tracking-widest mb-1.5 block">Email</label>
                <div className="relative">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5A5A6E]">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                      <polyline points="22,6 12,13 2,6" />
                    </svg>
                  </div>
                  <input id="login-email" type="email" value={email} onChange={e => setEmail(e.target.value)}
                    placeholder="you@company.com" required autoComplete="email"
                    className="w-full bg-[#1C1C24] border border-[rgba(255,255,255,0.07)] rounded-xl pl-9 pr-4 py-2.5 text-sm font-mono text-[#EEEEF0] placeholder-[#3A3A4A] focus:outline-none focus:border-[rgba(124,58,237,0.4)] transition-colors"
                  />
                </div>
              </div>

              {/* Password */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label htmlFor="login-password" className="text-[10px] font-mono text-[#5A5A6E] uppercase tracking-widest">Password</label>
                  <Link href="/auth/forgot-password" className="text-[10px] font-mono text-[#A78BFA] hover:text-[#EEEEF0] transition-colors">
                    Forgot password?
                  </Link>
                </div>
                <div className="relative">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5A5A6E]">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" />
                    </svg>
                  </div>
                  <input id="login-password" type={showPassword ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)}
                    placeholder="••••••••" required autoComplete="current-password"
                    className="w-full bg-[#1C1C24] border border-[rgba(255,255,255,0.07)] rounded-xl pl-9 pr-10 py-2.5 text-sm font-mono text-[#EEEEF0] placeholder-[#3A3A4A] focus:outline-none focus:border-[rgba(124,58,237,0.4)] transition-colors"
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#5A5A6E] hover:text-[#9999A8] transition-colors"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                        <line x1="1" y1="1" x2="23" y2="23" />
                      </svg>
                    ) : (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                        <circle cx="12" cy="12" r="3" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              {/* Error */}
              {error && (
                <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[rgba(239,68,68,0.08)] border border-[rgba(239,68,68,0.2)]">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#EF4444" strokeWidth="2.5" strokeLinecap="round">
                    <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
                  </svg>
                  <span className="text-[11px] font-mono text-[#EF4444]">{error}</span>
                </div>
              )}

              {/* Submit */}
              <button type="submit" disabled={loading}
                className="w-full flex items-center justify-center gap-2 py-3 rounded-xl font-manrope font-semibold text-sm transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed text-white"
                style={{
                  background:  loading ? 'rgba(124,58,237,0.3)' : 'linear-gradient(135deg, #7C3AED 0%, #A78BFA 100%)',
                  boxShadow:   loading ? 'none' : '0 0 20px rgba(124,58,237,0.3)',
                }}
              >
                {loading ? (
                  <>
                    <svg className="animate-spin" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                    </svg>
                    Signing in…
                  </>
                ) : (
                  <>
                    Sign in as {activeRole?.label}
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M5 12h14M12 5l7 7-7 7" />
                    </svg>
                  </>
                )}
              </button>
            </form>

            {/* Footer */}
            <p className="text-center text-[10px] font-mono text-[#5A5A6E]">
              Don&apos;t have an account?{' '}
              <Link href="/auth/signup" className="text-[#A78BFA] hover:text-[#EEEEF0] transition-colors">
                Create account
              </Link>
            </p>
          </div>
        </div>

        {/* Demo accounts — click to auto-fill */}
        <div className="mt-4 bg-[#0D0D12] border border-[rgba(255,255,255,0.06)] rounded-xl p-4">
          <p className="text-[10px] font-mono text-[#5A5A6E] uppercase tracking-widest mb-2.5">Demo accounts — click to fill</p>
          <div className="space-y-0.5">
            {demoAccounts.map(d => (
              <button key={d.email} type="button"
                onClick={() => { setEmail(d.email); setPassword('password123'); setSelectedRole(d.id); }}
                className="w-full flex items-center justify-between py-1.5 px-2 rounded-lg hover:bg-[rgba(255,255,255,0.03)] transition-colors text-left"
              >
                <span className="text-[11px] font-mono text-[#A78BFA]">{d.email}</span>
                <span className="text-[10px] font-mono text-[#5A5A6E]">{d.role}</span>
              </button>
            ))}
          </div>
          <p className="text-[9px] font-mono text-[#3A3A4E] mt-2">Password for all: <span className="text-[#5A5A6E]">password123</span></p>
        </div>

      </div>
    </div>
  );
}

export default function LoginPage() {
  return (
    <Suspense>
      <LoginContent />
    </Suspense>
  );
}
